import React from 'react';
import { AlertTriangle, Clock, LogOut, RefreshCw } from 'lucide-react';

interface IdleWarningModalProps {
  isOpen: boolean;
  timeRemaining: number;
  onContinue: () => void;
  onLogout: () => void;
}

export default function IdleWarningModal({
  isOpen,
  timeRemaining,
  onContinue,
  onLogout,
}: IdleWarningModalProps) {
  if (!isOpen) return null;

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getProgressPercentage = (): number => {
    const totalWarningTime = 120; // 2 minutes
    return ((totalWarningTime - timeRemaining) / totalWarningTime) * 100;
  };

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm' />

      {/* Modal */}
      <div className='relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all'>
        {/* Header */}
        <div className='flex items-center justify-center p-6 border-b border-gray-200 dark:border-gray-700'>
          <div className='flex items-center space-x-3'>
            <div className='flex-shrink-0'>
              <AlertTriangle className='h-8 w-8 text-amber-500' />
            </div>
            <div>
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>
                Session Timeout Warning
              </h3>
              <p className='text-sm text-gray-500 dark:text-gray-400'>
                You will be logged out due to inactivity
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className='p-6'>
          <div className='text-center mb-6'>
            <div className='flex items-center justify-center mb-4'>
              <Clock className='h-12 w-12 text-amber-500 mr-3' />
              <div className='text-4xl font-mono font-bold text-gray-900 dark:text-white'>
                {formatTime(timeRemaining)}
              </div>
            </div>

            {/* Progress bar */}
            <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4'>
              <div
                className='bg-amber-500 h-2 rounded-full transition-all duration-1000 ease-linear'
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>

            <p className='text-gray-600 dark:text-gray-300 text-sm'>
              Your session will expire in <strong>{formatTime(timeRemaining)}</strong> due to
              inactivity.
              <br />
              Click "Continue Session" to stay logged in.
            </p>
          </div>

          {/* Action buttons */}
          <div className='flex space-x-3'>
            <button
              onClick={onLogout}
              className='flex-1 flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors'
            >
              <LogOut className='h-4 w-4 mr-2' />
              Logout Now
            </button>

            <button
              onClick={onContinue}
              className='flex-1 flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors'
            >
              <RefreshCw className='h-4 w-4 mr-2' />
              Continue Session
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className='px-6 py-3 bg-gray-50 dark:bg-gray-700 rounded-b-lg'>
          <p className='text-xs text-gray-500 dark:text-gray-400 text-center'>
            💡 <strong>Tip:</strong> Your session will automatically extend when you're active
          </p>
        </div>
      </div>
    </div>
  );
}
